/**
 * Host-page scrolling for the web-component embed. There the widget shares the host page's document
 * and window, so it has to bring its own next step into view itself — below whatever fixed or sticky
 * chrome the host renders at the top of the viewport, which the widget cannot know ahead of time and
 * therefore hit-tests at runtime. All offsets are px: a host `html { font-size }` must not scale them.
 */

/** Trailing debounce that collapses a burst of step changes into one scroll. */
export const DEBOUNCE_MS = 600;
/** How long a user gesture inside the widget keeps scroll requests allowed. */
export const ARM_WINDOW_MS = 8000;
/** Breathing room below the host chrome and above the viewport bottom. */
export const GAP_PX = 16;
/** A step that already starts in this upper share of the visible band is left where it is. */
export const STEP_UPPER_ZONE_RATIO = 1 / 3;
export const MIN_SCROLL_DELTA_PX = 24;
export const SETTLE_POLL_MS = 50;
export const SETTLE_STABLE_POLLS = 4;
export const SETTLE_MAX_MS = 2500;
export const MAX_CORRECTIONS = 3;
export const CORRECTION_TOLERANCE_PX = 4;
/** A fixed or sticky element taller than this share of the viewport is an overlay, not chrome. */
export const CHROME_MAX_RATIO = 0.5;
export const CHROME_PROBE_PASSES = 3;

export interface VerticalRect {
  top: number;
  bottom: number;
}

/**
 * Scroll needed to bring a new step to just below the host chrome, or 0. A step above the band always
 * comes back (the one-page stack collapsed above it); a fully visible step, or one that already starts
 * in the upper part of the band, stays put.
 */
export function computeStepScrollDelta(rect: VerticalRect, chromeBottom: number, viewportHeight: number): number {
  const bandTop = chromeBottom + GAP_PX;
  let delta: number;
  if (rect.top < chromeBottom) {
    delta = rect.top - bandTop;
  } else if (rect.bottom <= viewportHeight || rect.top <= bandTop + (viewportHeight - bandTop) * STEP_UPPER_ZONE_RATIO) {
    delta = 0;
  } else {
    delta = rect.top - bandTop;
  }
  return Math.abs(delta) < MIN_SCROLL_DELTA_PX ? 0 : delta;
}

/**
 * Scroll needed to reveal content that appeared below the fold ("nearest"): just enough to show its
 * bottom, never pushing its top under the host chrome, and never upwards.
 */
export function computeRevealScrollDelta(rect: VerticalRect, chromeBottom: number, viewportHeight: number): number {
  const need = rect.bottom - (viewportHeight - GAP_PX);
  if (need <= 0) {
    return 0;
  }
  const delta = Math.min(need, rect.top - (chromeBottom + GAP_PX));
  return delta < MIN_SCROLL_DELTA_PX ? 0 : delta;
}

/** Whether an element can be scrolled to: attached, displayed and with a height. */
export function isRenderedTarget(element: HTMLElement | null | undefined): element is HTMLElement {
  return !!element
    && element.isConnected
    && getComputedStyle(element).display !== 'none'
    && element.getClientRects().length > 0
    && element.getBoundingClientRect().height > 0;
}

/**
 * Bottom edge (viewport px) of the host's fixed or sticky chrome at the top of the viewport, or 0.
 * Hit-tests three points across the widget's width and re-probes just below each hit to catch stacked
 * bars. The widget itself, its composed ancestors (a shadow-rooted widget is hit-tested as its host)
 * and overlays taller than CHROME_MAX_RATIO are ignored. Not seen: chrome with `pointer-events: none`
 * and chrome rendered inside a shadow root.
 */
export function measureHostChrome(widgetHost: HTMLElement, viewport: { width: number; height: number }): number {
  const maxChromeHeight = viewport.height * CHROME_MAX_RATIO;
  const widgetRect = widgetHost.getBoundingClientRect();
  const probeXs = [widgetRect.left + 8, (widgetRect.left + widgetRect.right) / 2, widgetRect.right - 8]
    .map(x => Math.min(Math.max(x, 0), viewport.width - 1));

  let chromeBottom = 0;
  let y = 1;
  for (let pass = 0; pass < CHROME_PROBE_PASSES; pass++) {
    let passBottom = chromeBottom;
    for (const x of probeXs) {
      for (const hit of document.elementsFromPoint(x, y)) {
        if (isHostChromeAt(hit, y, maxChromeHeight, widgetHost)) {
          passBottom = Math.max(passBottom, hit.getBoundingClientRect().bottom);
        }
      }
    }
    if (passBottom <= chromeBottom) {
      break;
    }
    chromeBottom = passBottom;
    y = chromeBottom + 1;
  }
  return Math.min(chromeBottom, maxChromeHeight);
}

function isHostChromeAt(element: Element, y: number, maxHeight: number, widgetHost: HTMLElement): boolean {
  const position = getComputedStyle(element).position;
  if (position !== 'fixed' && position !== 'sticky') {
    return false;
  }
  const rect = element.getBoundingClientRect();
  if (rect.top > y || rect.bottom <= y || rect.height > maxHeight) {
    return false;
  }
  return !widgetHost.contains(element) && !isComposedAncestor(element, widgetHost);
}

function isComposedAncestor(candidate: Element, node: Node): boolean {
  for (let current = composedParent(node); current; current = composedParent(current)) {
    if (current === candidate) {
      return true;
    }
  }
  return false;
}

function composedParent(node: Node): Node | null {
  return node.parentNode ?? (node as ShadowRoot).host ?? null;
}
